// src/components/SmartLists/SmartListsSheet.jsx
import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, TextField } from '@mui/material';
//import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import SmartListsSheetRow from './SmartListsSheetRow';
import {
  COLUMN_CONFIG,
  ROW_NUMBER_WIDTH,
  GROUP_HEADER_HEIGHT,
  COLUMN_HEADER_HEIGHT,
  getColumnGroupWidth,
} from './smartListsGridConfig';

/**
 * SmartListsSheet
 * -----------------------------------------------------------------------------
 * Unified grouped spreadsheet surface for SmartLists.
 *
 * PURPOSE
 *  • Render the SmartLists workspace as one coherent sheet
 *  • Keep row numbers, grouped field headers, column headers, and body rows
 *    aligned through one shared layout system
 *  • Preserve your original grouped-column requirement:
 *      - one field = one grouped block
 *      - each grouped block contains:
 *          Option | Hint | Status | Launch Date | End Date
 *
 * WHY THIS FILE EXISTS
 *  • SmartListsWorkspaceCanvas should not directly own every render concern
 *  • The spreadsheet structure needs a dedicated component that understands:
 *      - grouped field headers
 *      - spreadsheet header row
 *      - row-driven body
 *      - resizable column widths
 *
 * IMPORTANT
 *  • This is still a grouped sheet, not a flat table
 *  • Column widths are shared across every group for now
 *  • Row numbers are rendered as the first sticky sheet column
 *  • This file does NOT own edit state; it only renders the sheet structure
 *
 * FUTURE EVOLUTION
 *  • per-group hidden columns
 *  • sticky top-left corner cell
 *  • range selection overlays
 *  • multi-row resize handles
 * -----------------------------------------------------------------------------
 */

const GOOGLE_SHEETS_FOOTER_HEIGHT = 44;
const GOOGLE_SHEETS_ADD_BLUE = '#1a73e8';
const GOOGLE_SHEETS_FOOTER_BG = '#f1f3f4';
const GOOGLE_SHEETS_FOOTER_BORDER = '#dadce0';
const GOOGLE_SHEETS_TEXT = '#3c4043';

const resolveFieldTypeIcon = (fieldType) => {
  if (fieldType === 'multi-select') {
    return <CheckBoxIcon sx={{ fontSize: '0.95rem', color: '#455A64', mr: 1 }} />;
  }

  return <ArrowDropDownCircleIcon sx={{ fontSize: '0.95rem', color: '#455A64', mr: 1 }} />;
};

const SmartListsSheet = ({
  groups = [],
  rowCount = 0,

  selectedDraftId = null,
  selectedCell,

  registerCellRef,
  onSelectCell,
  onChangeValue,
  onKeyDown,
  onPaste,
  onSelectField,

  columnWidths = {},
  onStartResize,
  onAutoFitColumn,

  onAddRows,
  defaultAddRowsCount = 50,
}) => {
  /**
   * Build the effective column definitions using shared runtime widths.
   * All groups use the same spreadsheet columns and same width map.
   */
  const resolvedColumns = useMemo(() => {
    return COLUMN_CONFIG.map((column) => ({
      ...column,
      width: columnWidths?.[column.key] || column.width,
    }));
  }, [columnWidths]);

  /**
   * Total width for one field-group based on the current shared widths.
   */
  const groupWidth = useMemo(() => {
    return getColumnGroupWidth(columnWidths);
  }, [columnWidths]);

  /**
   * Google-Sheets-like footer control state
   * ---------------------------------------------------------------------------
   * This component should render the footer UX, but the actual row expansion
   * still belongs in the parent canvas/controller.
   *
   * We therefore keep only the small local input state here so the user can:
   *  • see a default row-add amount
   *  • click into the outlined numeric field
   *  • change the amount before clicking Add
   *
   * The parent decides what "add rows" actually means in the grid model.
   * ------------------------------------------------------------------------- */
  const [rowsToAddInput, setRowsToAddInput] = useState(String(defaultAddRowsCount));
  const [isAddHovered, setIsAddHovered] = useState(false);

  const handleRowsToAddChange = (event) => {
    const nextValue = event.target.value;

    // Keep the input numeric-ish without being overly destructive while typing.
    // This lets users clear and re-enter naturally.
    if (/^\d*$/.test(nextValue)) {
      setRowsToAddInput(nextValue);
    }
  };

  const handleAddRowsClick = () => {
    const parsed = Number.parseInt(rowsToAddInput, 10);
    const safeCount = Number.isFinite(parsed) && parsed > 0 ? parsed : defaultAddRowsCount;

    onAddRows?.(safeCount);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 'fit-content',
        minWidth: '100%',
        backgroundColor: '#fff',
      }}
    >
      {/* ---------------------------------------------------------------------
       * Top grouped field header row
       * ------------------------------------------------------------------- */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        {/* Top-left sticky spacer (above row numbers) */}
        <Box
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 6,
            width: `${ROW_NUMBER_WIDTH}px`,
            minWidth: `${ROW_NUMBER_WIDTH}px`,
            height: `${GROUP_HEADER_HEIGHT}px`,
            borderRight: '1px solid #e0e0e0',
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#f1f1f1',
            boxSizing: 'border-box',
          }}
        />

        {/* One grouped header per SmartList field */}
        {groups.map((group) => {
          const groupId = group?.localDraftId;
          if (!groupId) return null;

          const isSelectedGroup = selectedDraftId === groupId;

          return (
            <Box
              key={`${groupId}-group-header`}
              onClick={() => onSelectField?.(groupId)}
              sx={{
                width: `${groupWidth}px`,
                minWidth: `${groupWidth}px`,
                maxWidth: `${groupWidth}px`,
                height: `${GROUP_HEADER_HEIGHT}px`,
                px: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#f7f7f7',
                borderRight: '1px solid #e0e0e0',
                borderBottom: '1px solid #e0e0e0',
                boxSizing: 'border-box',
                cursor: 'pointer',
                boxShadow: isSelectedGroup ? 'inset 0 0 0 2px #1976d2' : 'none',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                {resolveFieldTypeIcon(group?.field?.type)}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {group?.field?.label || 'Untitled field'}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ---------------------------------------------------------------------
       * Spreadsheet column header row
       * ------------------------------------------------------------------- */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        {/* Sticky spacer above row numbers */}
        <Box
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 6,
            width: `${ROW_NUMBER_WIDTH}px`,
            minWidth: `${ROW_NUMBER_WIDTH}px`,
            height: `${COLUMN_HEADER_HEIGHT}px`,
            borderRight: '1px solid #e0e0e0',
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#f7f7f7',
            boxSizing: 'border-box',
          }}
        />

        {/* One set of spreadsheet column headers for each group */}
        {groups.map((group) => {
          const groupId = group?.localDraftId;
          if (!groupId) return null;

          return (
            <Box
              key={`${groupId}-column-headers`}
              sx={{
                display: 'flex',
                width: `${groupWidth}px`,
                minWidth: `${groupWidth}px`,
                maxWidth: `${groupWidth}px`,
                borderBottom: '1px solid #e0e0e0',
                boxSizing: 'border-box',
              }}
            >
              {resolvedColumns.map((column) => (
                <Box
                  key={`${groupId}-header-${column.key}`}
                  sx={{
                    width: `${column.width}px`,
                    minWidth: `${column.width}px`,
                    maxWidth: `${column.width}px`,
                    height: `${COLUMN_HEADER_HEIGHT}px`,
                    px: 1,
                    pr: '14px', // reserve space so text doesn't sit under resize handle
                    borderRight: '1px solid #e0e0e0',
                    boxSizing: 'border-box',
                    position: 'relative',
                    backgroundColor: '#f7f7f7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    overflow: 'hidden',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontStyle: 'italic',
                      lineHeight: 1,
                      display: 'block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      pr: '2px',
                    }}
                  >
                    {column.label}
                  </Typography>

                  {/* Spreadsheet-style resize handle */}
                  <Box
                    onMouseDown={(event) => onStartResize?.(event, column.key)}
                    onDoubleClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onAutoFitColumn?.(column.key);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '8px',
                      height: '100%',
                      cursor: 'col-resize',
                      zIndex: 2,
                      backgroundColor: 'transparent',
                    }}
                  />
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>

      {/* ---------------------------------------------------------------------
       * Spreadsheet body rows
       * ------------------------------------------------------------------- */}
      <Box>
        {Array.from({ length: rowCount }).map((_, rowIndex) => (
          <SmartListsSheetRow
            key={`smartlists-sheet-row-${rowIndex}`}
            rowIndex={rowIndex}
            groups={groups}
            selectedCell={selectedCell}
            registerCellRef={registerCellRef}
            onSelectCell={onSelectCell}
            onChangeValue={onChangeValue}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            columnWidths={columnWidths}
          />
        ))}
      </Box>

      {/* ---------------------------------------------------------------------
       * Google-Sheets-inspired bottom row growth footer
       * -------------------------------------------------------------------
       * UX GOALS
       *  • visually separate the control from the grid body with a light gray bar
       *  • keep the left sticky row-number lane visually continuous
       *  • present:
       *      Add [outlined numeric field] more rows at the bottom
       *  • make the Add affordance feel lightweight by default, but stronger
       *    on hover/focus like Google Sheets
       *
       * IMPORTANT
       *  • This footer only captures the requested count and emits it upward.
       *  • The parent canvas should remain the source of truth for how many
       *    visible rows are actually appended.
       * ------------------------------------------------------------------- */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          minHeight: `${GOOGLE_SHEETS_FOOTER_HEIGHT}px`,
          height: `${GOOGLE_SHEETS_FOOTER_HEIGHT}px`,
          backgroundColor: GOOGLE_SHEETS_FOOTER_BG,
          borderTop: `1px solid ${GOOGLE_SHEETS_FOOTER_BORDER}`,
          boxSizing: 'border-box',
        }}
      >
        {/* Sticky spacer aligned with row-number rail */}
        <Box
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 5,
            width: `${ROW_NUMBER_WIDTH}px`,
            minWidth: `${ROW_NUMBER_WIDTH}px`,
            height: `${GOOGLE_SHEETS_FOOTER_HEIGHT}px`,
            borderRight: `1px solid ${GOOGLE_SHEETS_FOOTER_BORDER}`,
            backgroundColor: GOOGLE_SHEETS_FOOTER_BG,
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        />

        {/* Footer content region */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            color: GOOGLE_SHEETS_TEXT,
          }}
        >
          {/* Add affordance
              -----------------------------------------------------------------
              We use a text-style button so the resting state feels like the
              subtle blue "Add" text in Google Sheets. On hover/focus we give
              it a slightly stronger background/border radius treatment so it
              becomes more button-like without turning it into a heavy CTA.
              ----------------------------------------------------------------- */}
          <Button
            onClick={handleAddRowsClick}
            onMouseEnter={() => setIsAddHovered(true)}
            onMouseLeave={() => setIsAddHovered(false)}
            variant="text"
            size="small"
            disableElevation
            sx={{
              minWidth: 0,
              px: 0.75,
              py: 0.25,
              lineHeight: 1.2,
              fontSize: '0.8125rem',
              fontWeight: 700,
              textTransform: 'none',
              color: GOOGLE_SHEETS_ADD_BLUE,
              borderRadius: '4px',
              backgroundColor: isAddHovered ? 'rgba(26, 115, 232, 0.08)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(26, 115, 232, 0.08)',
              },
              '&:focus-visible': {
                backgroundColor: 'rgba(26, 115, 232, 0.12)',
              },
            }}
          >
            Add
          </Button>

          {/* Row count input
              -----------------------------------------------------------------
              Google Sheets uses a compact outlined numeric field here.
              We mimic that with a tight MUI outlined TextField.
              ----------------------------------------------------------------- */}
          <TextField
            value={rowsToAddInput}
            onChange={handleRowsToAddChange}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleAddRowsClick();
              }
            }}
            variant="outlined"
            size="small"
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              'aria-label': 'Number of rows to add',
              style: {
                textAlign: 'center',
                paddingTop: '6px',
                paddingBottom: '6px',
                fontSize: '0.8125rem',
                fontWeight: 700,
              },
            }}
            sx={{
              width: '88px',
              minWidth: '88px',
              '& .MuiOutlinedInput-root': {
                height: '30px',
                backgroundColor: '#fff',
                borderRadius: '4px',
                '& fieldset': {
                  borderColor: '#c4c7c5',
                },
                '&:hover fieldset': {
                  borderColor: '#9aa0a6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: GOOGLE_SHEETS_ADD_BLUE,
                  borderWidth: '1px',
                },
              },
            }}
          />

          <Typography
            variant="body2"
            sx={{
              fontSize: '0.8125rem',
              color: GOOGLE_SHEETS_TEXT,
              whiteSpace: 'nowrap',
            }}
          >
            more rows at the bottom
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SmartListsSheet;